import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReferenceData } from '../../../shared/contexts/ReferenceDataContext';
import { createTemporaryReferenceData, isImageFile, createObjectURLFromFile } from '../utils/referenceNavigation';
import { isSingleURL, isShareableURL, getDomainFromURL } from '../utils/urlValidation';
import { toast } from 'sonner';

export function useGlobalMediaHandlers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTemporaryData } = useReferenceData();

  const handlePasteEvent = useCallback((event: ClipboardEvent) => {
    // Don't intercept paste events if user is typing in an input field
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }

    const items = event.clipboardData?.items;
    if (!items) return;

    // Check for images first (existing functionality)
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          console.log('Paste event - got image blob:', blob);
          
          // Prevent default paste behavior
          event.preventDefault();
          
          // Create object URL for preview
          const objectUrl = createObjectURLFromFile(blob);
          console.log('Created object URL:', objectUrl);
          
          // Create temporary reference data
          const tempData = createTemporaryReferenceData({
            initialImage: objectUrl,
            imageBlob: blob,
            source: 'paste'
          });
          console.log('Created temporary data:', tempData);
          
          // Store the data and navigate
          setTemporaryData(tempData);
          void navigate('/new-reference');
          
          toast.success('Image pasted! Redirecting to create reference...');
        }
        return; // Exit early if we found an image
      }
    }

    // Check for text/URL content if no images were found
    for (const item of items) {
      if (item.type === 'text/plain') {
        item.getAsString((text) => {
          console.log('[URL Detection] Paste event - got text:', text);
          
          const urlCheck = isSingleURL(text);
          if (urlCheck.isURL && urlCheck.url && isShareableURL(urlCheck.url)) {
            console.log('[URL Detection] Detected valid URL:', urlCheck.url);
            
            // Prevent default paste behavior
            event.preventDefault();
            
            const domain = getDomainFromURL(urlCheck.url);
            console.log('[URL Detection] Extracted domain:', domain);
            
            // Create temporary reference data for URL
            const tempData = createTemporaryReferenceData({
              sourceURL: urlCheck.url,
              title: `Reference from ${domain}`,
              source: 'paste'
            });
            console.log('[URL Detection] Created temporary URL data:', tempData);
            
            // Store the data and navigate
            console.log('[URL Detection] Storing temporary data and navigating to /new-reference');
            setTemporaryData(tempData);
            void navigate('/new-reference');
            
            console.log('[URL Detection] Showing success toast for URL paste');
            toast.success(`URL pasted from ${domain}! Extracting metadata...`);
          } else {
            console.log('[URL Detection] Text is not a valid/shareable URL:', {
              isURL: urlCheck.isURL,
              url: urlCheck.url,
              isShareable: urlCheck.url ? isShareableURL(urlCheck.url) : false
            });
          }
        });
        break; // Only process the first text item
      }
    }
  }, [navigate, setTemporaryData]);

  const handleDragOver = useCallback((event: DragEvent) => {
    // Check if the drag contains files
    if (event.dataTransfer?.types.includes('Files')) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      
      // Add visual feedback
      document.body.classList.add('drag-active');
    }
  }, []);

  const handleDragLeave = useCallback((event: DragEvent) => {
    // Only remove the class if we're leaving the document body
    if (event.target === document.body) {
      document.body.classList.remove('drag-active');
    }
  }, []);

  const handleDrop = useCallback((event: DragEvent) => {
    event.preventDefault();
    document.body.classList.remove('drag-active');
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isImageFile(file)) {
        // Create object URL for preview
        const objectUrl = createObjectURLFromFile(file);
        
        // Create temporary reference data
        const tempData = createTemporaryReferenceData({
          initialImage: objectUrl,
          imageBlob: file,
          source: 'drag-drop'
        });
        
        // Store the data and navigate
        setTemporaryData(tempData);
        void navigate('/new-reference');
        
        toast.success('Image dropped! Redirecting to create reference...');
      } else {
        toast.error('Please drop an image file');
      }
    }
  }, [navigate, setTemporaryData]);

  // Mobile-specific touch handlers for drag-and-drop simulation
  const handleTouchStart = useCallback((event: TouchEvent) => {
    // Only handle if we're touching a file input or similar element
    const target = event.target as HTMLElement;
    if (target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file') {
      return; // Let the native file input handle this
    }
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    // Prevent default scrolling when dragging files on mobile
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      if (element && element.classList.contains('drop-zone')) {
        event.preventDefault();
      }
    }
  }, []);

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    // Clean up any drag state
    document.body.classList.remove('drag-active');
  }, []);

  // Check if device supports clipboard API
  const supportsClipboard = useCallback(() => {
    return Boolean(navigator.clipboard && typeof navigator.clipboard.read === 'function');
  }, []);

  // Check if device is mobile
  const isMobile = useCallback(() => {
    const userAgent = navigator.userAgent;
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
           maxTouchPoints > 2;
  }, []);

  // Check if device can use drag and drop
  const canUseDragDrop = useCallback(() => {
    const userAgent = navigator.userAgent;
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
                          maxTouchPoints > 2;
    return !isMobileDevice || maxTouchPoints > 2; // Allow on tablets
  }, []);

  useEffect(() => {
    // Only add listeners if we're not already on the new-reference page
    if (location.pathname !== '/new-reference') {
      // Always add paste listener (works on most modern mobile browsers)
      document.addEventListener('paste', handlePasteEvent);
      
      // Add drag-and-drop listeners for desktop and tablets
      document.addEventListener('dragover', handleDragOver);
      document.addEventListener('dragleave', handleDragLeave);
      document.addEventListener('drop', handleDrop);

      // Add touch event listeners for mobile devices
      const isMobileDevice = isMobile();
      if (isMobileDevice) {
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });
        
        // Show a helpful message for mobile users about paste functionality
        if (supportsClipboard()) {
          console.log('Mobile device detected with clipboard support - paste functionality available');
        } else {
          console.log('Mobile device detected - limited paste functionality');
        }
      }

      return () => {
        document.removeEventListener('paste', handlePasteEvent);
        document.removeEventListener('dragover', handleDragOver);
        document.removeEventListener('dragleave', handleDragLeave);
        document.removeEventListener('drop', handleDrop);
        
        if (isMobileDevice) {
          document.removeEventListener('touchstart', handleTouchStart);
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
        }
        
        // Clean up any drag state
        document.body.classList.remove('drag-active');
      };
    }
  }, [location.pathname, handlePasteEvent, handleDragOver, handleDragLeave, handleDrop, 
      handleTouchStart, handleTouchMove, handleTouchEnd, isMobile, supportsClipboard]);

  // Return utility functions for components to use
  return {
    isMobile: isMobile(),
    supportsClipboard: supportsClipboard(),
    canUseDragDrop: canUseDragDrop()
  };
} 